/**
 * The whole game: menus -> generator -> mission -> report -> aftermath -> menus. The roguelike
 * layer (src/meta) owns every decision; this file only moves data between the four parts and
 * keeps the suspend save, so that closing the tab mid-mission never rewinds a death.
 */

import type { WebGLRenderer } from 'three';
import { createAudio } from '../audio';
import { generateMission } from '../gen';
import { trainingCourse } from '../gen/training';
import { getBindings } from '../input/bindings';
import { createGameApi, type OperationState } from '../meta';
import type { Deployment } from '../meta/api';
import type { MissionSummary } from '../meta/types';
import { CLOUD_KEY, type CloudSave, createSyncedStorage, startCloudSave } from '../platform/cloudSave';
import { cameraFor, startPhotos } from '../platform/photos';
import { browserStorage } from '../platform/storage';
import { startWall } from '../platform/wall';
import type { MissionResult } from '../sim/types';
import { createMenus, getMenuSettings, type Settings, type SuspendedMission } from '../ui/screens';
import { markTrainingDone } from '../ui/screens/trainingFlag';
import { wireMenuSounds } from './menuSounds';
import { type MissionHandle, startMission, type Suspended } from './missionApp';
import { missionSettings } from './missionSettings';
import { MusicDirector } from './musicDirector';
import { num } from './params';
import { saveLatestReplay } from './replay';
import { clearSuspend, loadSuspend, saveSuspend } from './suspend';
import { createTrainingCoach, settleFlight, stoppedResult } from './training';

const SUSPEND_EVERY_MS = 5000;

function freshSeed(): number {
  const box = new Uint32Array(1);
  crypto.getRandomValues(box);
  return box[0] ?? 1;
}

/**
 * Saves may be mirrored to the server the page came from (docs/deploy.md). The newest copy has to
 * be in localStorage BEFORE anything reads it, so the game starts when the pull has settled -
 * which, where there is no save server, is as soon as one small request has failed.
 */
export function startGame(canvas: HTMLCanvasElement, renderer: WebGLRenderer): void {
  void startCloudSave().then((cloud) => runGame(canvas, renderer, cloud));
}

function runGame(canvas: HTMLCanvasElement, renderer: WebGLRenderer, cloud: CloudSave): void {
  // Cloud saves off: the very storage object the game has always used, and `cloud.touch` does nothing.
  const api = createGameApi(cloud.enabled() ? createSyncedStorage(browserStorage, cloud) : browserStorage, freshSeed);
  const audio = createAudio();
  const settings = getMenuSettings();
  // One key-binding store for the game: the Controls screen edits it, every mission reads it.
  const bindings = getBindings();
  // Music files in /music play where there is one for the moment; the synthesised score elsewhere.
  const music = new MusicDirector(audio);
  const applySettings = (s: Settings): void => music.applySettings(s);
  applySettings(settings.get());
  settings.subscribe(applySettings);
  // These two stores write localStorage themselves: tell the cloud layer when they have, rather than rewrite them.
  settings.subscribe(() => cloud.touch(CLOUD_KEY.settings));
  bindings.subscribe(() => cloud.touch(CLOUD_KEY.bindings));
  // Browsers keep audio locked until a gesture; any of these will do, and resume() is idempotent.
  for (const gesture of ['pointerdown', 'keydown'] as const) window.addEventListener(gesture, () => music.unlock());
  // PHOTOS: the picture taken as a ranger falls - kept on this device, sent up under the save code, shown by the menus.
  const photos = startPhotos(cloud);

  let mission: MissionHandle | null = null;
  let flying: Deployment | null = null;
  let suspendTimer = 0;

  const suspend = (): void => {
    const live = mission?.snapshot();
    if (!live || !flying || flying.mode === 'practice' || flying.mode === 'training') return;
    saveSuspend(flying, live);
    // suspend.ts writes localStorage itself; the cloud layer only needs to hear that it did.
    cloud.touch(CLOUD_KEY.suspend);
  };
  /** The page is going away, or out of sight: the suspend save first, then whatever is still waiting to be sent. */
  const leave = (): void => {
    suspend();
    cloud.flush();
  };

  function stand(): void {
    window.clearInterval(suspendTimer);
    mission?.dispose();
    mission = null;
    flying = null;
  }

  function fly(deployment: Deployment, resume?: Suspended): void {
    menus.hide();
    audio.stopMusic();
    flying = deployment;
    // ROUND7: the training course is hand-built; everything else is generated from its params.
    const training = deployment.mode === 'training';
    const def = training ? trainingCourse() : generateMission(deployment.params);
    music.mission(def);
    const practice = deployment.mode === 'practice' || training;
    const flightSettings = missionSettings(settings.get(), api.profile().operation?.difficulty ?? undefined);
    const options: Parameters<typeof startMission>[2] = {
      def,
      loadout: deployment.loadout,
      mods: deployment.mods,
      skipInsertion: false,
      pitch: num('pitch', 45),
      ppm: num('ppm', 18),
      showHud: true,
      audio,
      bindings,
      settings: flightSettings,
      hidePlayfieldOnPause: deployment.mode === 'daily',
      // There is no abort (08 §2.8): a mission with a ranger's life on it can only be put down and picked up.
      quit: { label: training ? 'END TRAINING' : practice ? 'END PRACTICE' : 'SUSPEND & QUIT', go: quit },
      onEnd: (result, summary) => land(deployment, result, summary),
    };
    if (resume) options.resume = resume;
    // Only a death that puts a name on the Memorial Wall is photographed: an operation's, or Classic's.
    const camera = cameraFor(deployment.mode, photos.camera);
    if (camera) options.camera = camera;
    // ROUND7 §5: the coach talks the trainee round the course, and stops it if the garrison ever reaches alarm 3.
    if (training) {
      options.coach = createTrainingCoach({
        bindings, settings: flightSettings, onStop: (note) => stopTraining(deployment, note),
      });
    }
    mission = startMission(canvas, renderer, options);
    suspendTimer = window.setInterval(suspend, SUSPEND_EVERY_MS);
  }

  /** ROUND7 §5: the course was stopped before its end (full alarm, or the Osprey left): the end card says why. */
  function stopTraining(deployment: Deployment, note: string): void {
    if (flying !== deployment) return;
    stand();
    music.setOutcome(false);
    menus.showAftermath(deployment, stoppedResult(), null, note);
  }

  function quit(): void {
    suspend();
    stand();
    menus.showTitle();
  }

  function land(deployment: Deployment, result: MissionResult, summary: MissionSummary): void {
    // The mission is over, so its suspend save goes - unless this was the training course, which a held ranger's
    // save must outlive (ROUND7 §5: TRAINING stays on the title while a mission is suspended).
    settleFlight(deployment.mode, result.outcome, {
      clearSuspend,
      touchSuspend: () => cloud.touch(CLOUD_KEY.suspend),
      markTrainingDone,
    });
    // The last mission flown can be watched again at /?replay=latest (a resumed mission has no recording).
    const recording = mission?.recording();
    // A training replay would regenerate a depot from its params, not the course: it is not kept.
    if (recording && deployment.mode !== 'training') {
      saveLatestReplay({
        version: 1, source: { kind: 'generated', params: deployment.params }, loadout: deployment.loadout,
        mods: deployment.mods, skipInsertion: false, ...recording,
      });
    }
    stand();
    music.setOutcome(result.outcome === 'extracted' && result.objectiveComplete);
    // Practice changes nothing, so there is nothing to report and no aftermath to walk.
    const aftermath = deployment.mode === 'practice' || deployment.mode === 'training' ? null : api.report(deployment, summary);
    menus.showAftermath(deployment, result, aftermath);
  }

  /** The mission a ranger is still in, for the title: the suspend save, or a deployment that never got one. */
  function held(): SuspendedMission | null {
    const saved = loadSuspend();
    const operation = api.profile().operation as OperationState | null;
    const pending = operation?.deployed?.deployment ?? null;
    // Classic and Daily Drop missions are not pending anywhere in the profile: the suspend save is all there is.
    const mode = saved?.deployment.mode;
    if (saved && (mode === 'classic' || mode === 'daily' || pending)) {
      const minutes = Math.ceil(saved.world.clockTicks / 3600);
      return { label: `A RANGER IS STILL IN THE FIELD - ${minutes} MIN ON THE CLOCK.`, resume: () => fly(saved.deployment, saved) };
    }
    // Deployed, but the game closed before the first save: the aircraft is still inbound.
    if (pending) return { label: 'A RANGER IS ABOARD THE AIRCRAFT.', resume: () => fly(pending) };
    return null;
  }

  const menus = createMenus(document.body, api, (deployment) => fly(deployment), held, {
    bindings, cloud, wall: startWall(cloud), photos: photos.port,
  });
  wireMenuSounds(audio, music);
  window.addEventListener('pagehide', leave);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) leave();
  });

  if (held() === null) {
    clearSuspend();
    cloud.touch(CLOUD_KEY.suspend);
  }
  menus.showTitle();
}
